import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { LocalizedRef } from "@/lib/api/loads";
import { getTruckTypes } from "@/lib/api/trucks";
import { createTruckType, updateTruckType } from "@/lib/api/catalogs";
import {
  LocalizedNamesFields,
  toLocalizedName,
  type LocalizedNamesDraft,
} from "@/components/admin/catalogs/localized-names";
import { Text } from "@/components/ui/text";
import { CatalogPageTemplate } from "@/components/admin/catalog-page-template";

type TruckTypeRow = LocalizedRef & {
  created_at?: string;
  is_deleted?: boolean;
};

function AdminTruckTypesCatalogPage() {
  const { t: tAdminNav } = useTranslation("adminNav");
  const { t: tCatalogLoadTypes } = useTranslation("admin.catalogs.loadTypes");
  const { t: tCommon } = useTranslation("common");

  return (
    <CatalogPageTemplate<TruckTypeRow>
      title={tAdminNav("truckTypes")}
      tableId="catalog-truck-types"
      queryKey={["admin", "catalogs", "truck-types"]}
      fetchFn={getTruckTypes}
      columns={[
        {
          key: "nameEn",
          header: tCatalogLoadTypes("columns.nameEn"),
          defaultWidth: 200,
          minWidth: 100,
          render: (item, isDeleted) => (
            <Text
              weight="medium"
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.names?.en ?? item.name}
            </Text>
          ),
        },
        {
          key: "nameRu",
          header: tCatalogLoadTypes("columns.nameRu"),
          defaultWidth: 180,
          minWidth: 100,
          render: (item, isDeleted) => (
            <Text
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.names?.ru ?? "—"}
            </Text>
          ),
        },
        {
          key: "nameUz",
          header: tCatalogLoadTypes("columns.nameUz"),
          defaultWidth: 180,
          minWidth: 100,
          render: (item, isDeleted) => (
            <Text
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.names?.uz ?? "—"}
            </Text>
          ),
        },
      ]}
      searchFilter={(item, q) => {
        const names = [
          item.name,
          item.names?.en,
          item.names?.ru,
          item.names?.uz,
          item.names?.tr,
          item.names?.uz_cyr,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return names.includes(q);
      }}
      searchPlaceholder={`${tCommon("search")}...`}
      emptyMessage={tCommon("noResults")}
      addLabel={tCommon("add")}
      editTitle={`${tCommon("edit")} ${tAdminNav("truckTypes")}`}
      addTitle={`${tCommon("add")} ${tAdminNav("truckTypes")}`}
      failedToLoadMessage={tCommon("errorLoadingData")}
      failedToSaveMessage={tCommon("errorOccurred")}
      deleteEndpoint="/truck-types"
      editLabel={tCommon("edit")}
      itemToFormData={(item) => ({
        namesDraft: {
          en: item.names?.en ?? item.name ?? "",
          ru: item.names?.ru ?? "",
          uz: item.names?.uz ?? "",
        } as LocalizedNamesDraft,
      })}
      emptyFormData={() => ({
        namesDraft: { en: "", ru: "", uz: "" } as LocalizedNamesDraft,
      })}
      formDataToPayload={(fd) => ({
        name: (fd.namesDraft as LocalizedNamesDraft).en,
        names: toLocalizedName(fd.namesDraft as LocalizedNamesDraft),
      })}
      isFormValid={(fd) =>
        Boolean((fd.namesDraft as LocalizedNamesDraft).en.trim())
      }
      createFn={(payload) =>
        createTruckType(payload as Parameters<typeof createTruckType>[0])
      }
      updateFn={(id, payload) =>
        updateTruckType(id, payload as Parameters<typeof updateTruckType>[1])
      }
      renderForm={(formData, setFormData, submitting) => (
        <LocalizedNamesFields
          value={formData.namesDraft as LocalizedNamesDraft}
          onChange={(next) =>
            setFormData((prev) => ({ ...prev, namesDraft: next }))
          }
          disabled={submitting}
          testIdPrefix="admin-truck-type"
        />
      )}
      testIdPrefix="admin-catalog-truck-types"
    />
  );
}

export const Route = createFileRoute("/_app/admin/catalogs/truck-types")({
  component: AdminTruckTypesCatalogPage,
});
